import { useEffect, useRef, useCallback, useState, useMemo, memo } from 'react';
import { Terminal, type ILinkProvider, type ILink } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
// WebGL renderer disabled — causes cursor position bugs on some systems.
// import { WebglAddon } from '@xterm/addon-webgl';
import '@xterm/xterm/css/xterm.css';
import { useBackend } from '@/services/backend';
import { useTerminalStore } from '@/stores/terminalStore';
import { useThemeStore } from '@/stores/themeStore';
import { useFileOpenStore } from '@/stores/fileOpenStore';
import { useInputHistoryStore } from '@/stores/inputHistoryStore';
import { logger } from '@/lib/logger';
import { uint8ToBase64, writeTextToPty } from '@/lib/base64';
import { CefBackend } from '@/services/backend';

/**
 * Copy/paste via the host's OS clipboard in the CEF shell (navigator.clipboard
 * is blocked in windowless OSR — "Document is not focused"), falling back to the
 * Web Clipboard API on the Tauri/web shells.
 */
async function clipboardWrite(backend: unknown, text: string): Promise<void> {
  if (backend instanceof CefBackend) {
    await backend.clipboard.writeText(text);
    return;
  }
  await navigator.clipboard.writeText(text);
}
async function clipboardRead(backend: unknown): Promise<string> {
  if (backend instanceof CefBackend) return backend.clipboard.readText();
  return navigator.clipboard.readText();
}

/**
 * Clipboard IMAGE as base64 PNG, or '' when there's no image. Only the CEF
 * shell can do this — it reads the OS clipboard host-side, because OSR blocks
 * navigator.clipboard.read() ("Document is not focused"). On the web/Tauri
 * shells the native `paste` event already carries image items, which the
 * onPaste handler below covers, so '' here is correct.
 */
async function clipboardReadImage(backend: unknown): Promise<string> {
  if (backend instanceof CefBackend) return backend.clipboard.readImage();
  return '';
}

/**
 * Single-write threshold (in bytes/chars). Anything larger gets chunked
 * via writeTextToPty so we don't overflow the PTY input buffer / shell
 * line editor (PowerShell in particular drops the start of large pastes).
 * Tuned low enough to catch multi-line pastes, high enough to let normal
 * typing through on the fast path without artificial delays.
 */
const PTY_WRITE_CHUNK_THRESHOLD = 256;
import {
  isImageFile,
  isImagePath,
  saveImageAsAttachment,
  importImagePathAsAttachment,
  projectIdFromPath,
  quoteIfSpaced,
} from '@/lib/terminalImagePaste';

const TAG = 'TerminalView';

/** Buttons shown in the mobile toolbar */
type MobileKey =
  | { label: string; data: string; action?: undefined }
  | { label: string; action: 'scrollToTop' | 'scrollToBottom'; data?: undefined };

const MOBILE_KEYS: MobileKey[] = [
  { label: 'Esc', data: '\x1b' },
  { label: 'Tab', data: '\t' },
  { label: 'Ctrl+C', data: '\x03' },
  { label: 'Ctrl+D', data: '\x04' },
  { label: 'Ctrl+Z', data: '\x1a' },
  { label: '\u2191', data: '\x1b[A' },  // Up arrow
  { label: '\u2193', data: '\x1b[B' },  // Down arrow
  { label: '\u2190', data: '\x1b[D' },  // Left arrow
  { label: '\u2192', data: '\x1b[C' },  // Right arrow
  { label: 'Home', action: 'scrollToTop' },
  { label: 'End', action: 'scrollToBottom' },
];

interface TerminalViewProps {
  sessionId: number;
  isActive?: boolean;
  isAgent?: boolean;
  /** Project's agent_type when the session was launched (e.g. 'claude',
   *  'opencode', 'codex', 'gemini'). Lets the cursor-hide treatment
   *  apply only to Ink-based TUIs (Claude) that render their own cursor;
   *  other agents keep the standard blinking xterm cursor so the user
   *  can see where they're typing. */
  agentType?: string;
}

const THEME_DARK = {
  background: '#1c1917',   // stone-900
  foreground: '#e7e5e4',   // stone-200
  cursor: '#f59e0b',       // amber-500
  cursorAccent: '#1c1917',
  selectionBackground: '#44403c', // stone-700
  black: '#1c1917',
  red: '#ef4444',
  green: '#22c55e',
  yellow: '#eab308',
  blue: '#3b82f6',
  magenta: '#a855f7',
  cyan: '#06b6d4',
  white: '#e7e5e4',
  brightBlack: '#78716c',
  brightRed: '#f87171',
  brightGreen: '#4ade80',
  brightYellow: '#fbbf24',
  brightBlue: '#60a5fa',
  brightMagenta: '#c084fc',
  brightCyan: '#22d3ee',
  brightWhite: '#fafaf9',
};

// Light terminal palette — the terminal is often the primary pane (agents run
// in it), so a dark slab inside the light theme reads as broken. ANSI colors are
// the -600/-700 Tailwind shades so they stay legible on the light background.
const THEME_LIGHT = {
  background: '#fafaf9',   // stone-50
  foreground: '#1c1917',   // stone-900
  cursor: '#d97706',       // amber-600
  cursorAccent: '#fafaf9',
  selectionBackground: '#e7e5e4', // stone-200
  black: '#292524',        // stone-800
  red: '#dc2626',
  green: '#16a34a',
  yellow: '#ca8a04',
  blue: '#2563eb',
  magenta: '#9333ea',
  cyan: '#0891b2',
  white: '#57534e',        // stone-600
  brightBlack: '#57534e',  // stone-600
  brightRed: '#b91c1c',
  brightGreen: '#15803d',
  brightYellow: '#a16207',
  brightBlue: '#1d4ed8',
  brightMagenta: '#7e22ce',
  brightCyan: '#0e7490',
  brightWhite: '#1c1917',  // stone-900
};

function TerminalViewLoading() {
  return (
    <div className="flex-1 flex items-center justify-center bg-base-100 text-base-content/50 text-sm gap-2">
      <span className="loading loading-spinner loading-sm" />
      Starting terminal...
    </div>
  );
}

export default memo(function TerminalView({ sessionId, isActive }: TerminalViewProps) {
  // Placeholder sessions (negative ID) are optimistic tabs waiting for the
  // PTY daemon to respond. Show a loading indicator instead of mounting xterm.
  const isPending = sessionId < 0;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const backend = useBackend();
  const updateSessionStatus = useTerminalStore((s) => s.updateSessionStatus);
  const appTheme = useThemeStore((s) => s.theme);
  const xtermTheme = appTheme === 'atelier-light' ? THEME_LIGHT : THEME_DARK;
  const [dragOver, setDragOver] = useState(false);
  // Right-click context menu (Copy / Paste / Select All) — the windowless OSR
  // host shows no native menu, so the terminal renders its own.
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; hasSelection: boolean } | null>(null);
  const isTouchDevice = useMemo(() => 'ontouchstart' in window || navigator.maxTouchPoints > 0, []);

  // Re-theme a live terminal when the app theme flips, without tearing down
  // xterm (which would lose scrollback). xterm applies options.theme on assign.
  useEffect(() => {
    const term = termRef.current;
    if (term) term.options.theme = xtermTheme;
  }, [xtermTheme]);

  /** Write a string to the PTY as base64-encoded UTF-8.
   *  Chunks large writes so the shell's input buffer doesn't drop the
   *  beginning of long pastes — the regression the user kept hitting where
   *  only the last few lines made it through. */
  // When the daemon no longer has this session ("No session N" errors), flip
  // the tab to closed ONCE and show a clear end-of-session banner instead of
  // letting every resize/write spam the console against a dead PTY (the
  // post-reload "crashed terminal" failure mode).
  const sessionGoneRef = useRef(false);
  const handleSessionGone = useCallback((e: unknown): boolean => {
    const msg = e instanceof Error ? e.message : String(e);
    if (!/no session/i.test(msg)) return false;
    if (!sessionGoneRef.current) {
      sessionGoneRef.current = true;
      updateSessionStatus(sessionId, 'closed');
      termRef.current?.write('\r\n\x1b[90m[Session ended — the shell no longer exists. Close this tab or open a new terminal.]\x1b[0m\r\n');
    }
    return true;
  }, [sessionId, updateSessionStatus]);

  const writeToPty = useCallback((text: string) => {
    if (text.length <= PTY_WRITE_CHUNK_THRESHOLD) {
      const encoder = new TextEncoder();
      const bytes = encoder.encode(text);
      const encoded = uint8ToBase64(bytes);
      backend.terminal.write(sessionId, encoded).catch((e) => {
        if (!handleSessionGone(e)) logger.warn(TAG, 'PTY write failed', e);
      });
      return;
    }
    writeTextToPty(text, sessionId, (id, data) => backend.terminal.write(id, data))
      .catch((e) => {
        if (!handleSessionGone(e)) logger.warn(TAG, 'chunked PTY write failed', e);
      });
  }, [backend, sessionId, handleSessionGone]);

  /**
   * Paste the OS clipboard into the PTY. If the clipboard holds an IMAGE, save
   * it as a project attachment (`<project>/.ateliercode/attachments/`) and
   * insert the resulting file PATH instead — that's how an agent can actually
   * read a pasted screenshot, and it mirrors what the drag-drop and native
   * paste-event paths already do. Falls back to a normal text paste.
   */
  const pasteFromClipboard = useCallback(async () => {
    const term = termRef.current;
    if (!term) return;
    const sess = useTerminalStore.getState().sessions.get(sessionId);

    try {
      const imageB64 = await clipboardReadImage(backend);
      if (imageB64) {
        const projectId = sess?.projectPath ? projectIdFromPath(sess.projectPath) : null;
        if (projectId) {
          const saved = await backend.files.saveAttachment(
            projectId,
            `pasted-${Date.now()}.png`,
            imageB64,
          );
          if (saved) {
            term.paste(quoteIfSpaced(saved));
            return;
          }
        } else {
          logger.warn(TAG, 'clipboard image ignored — no project for this session');
        }
      }
    } catch (e) {
      // Image path failed (old host, oversized, no project…) — fall back to text.
      logger.warn(TAG, 'clipboard image paste failed', e);
    }

    const text = await clipboardRead(backend);
    if (!text) return;
    if (sess?.projectPath) {
      useInputHistoryStore.getState().addEntry(text, 'paste', sess.projectPath);
    }
    term.paste(text);
  }, [backend, sessionId]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('text/plain') || e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const session = useTerminalStore.getState().sessions.get(sessionId);
    const projectPath = session?.projectPath;
    const projectId = projectPath ? projectIdFromPath(projectPath) : null;

    // Case 1: real File objects (dragged from OS file explorer). If any are
    // images, upload as attachments so the agent actually receives the
    // content — otherwise fall through to path insertion.
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const pathsToInsert: string[] = [];
      let uploadedAnyImage = false;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (projectId && isImageFile(file)) {
          try {
            const saved = await saveImageAsAttachment(file, projectId, backend);
            if (saved) {
              pathsToInsert.push(quoteIfSpaced(saved));
              uploadedAnyImage = true;
              continue;
            }
          } catch (err) {
            logger.warn(TAG, 'Image drop upload failed', err);
          }
        }
        // Non-image or upload failed — fall back to the original OS path
        const osPath = (file as File & { path?: string }).path;
        if (osPath) pathsToInsert.push(quoteIfSpaced(osPath));
      }

      if (pathsToInsert.length > 0) {
        writeToPty(pathsToInsert.join(' '));
        termRef.current?.focus();
        if (uploadedAnyImage) return;
      }
      if (pathsToInsert.length > 0) return;
    }

    // Case 2: text/plain drop — could be a path from the in-app Files panel.
    // If it looks like an image path, read it and re-save as an attachment.
    const textPath = e.dataTransfer.getData('text/plain');
    if (textPath) {
      if (projectId && isImagePath(textPath)) {
        try {
          const saved = await importImagePathAsAttachment(textPath, projectId, backend);
          if (saved) {
            writeToPty(quoteIfSpaced(saved));
            termRef.current?.focus();
            return;
          }
        } catch (err) {
          logger.warn(TAG, 'Image path import failed', err);
        }
      }
      writeToPty(quoteIfSpaced(textPath));
      termRef.current?.focus();
    }
  }, [writeToPty, backend, sessionId]);

  /** Safely call fit() — swallows errors when container is hidden/zero-sized */
  const doFit = useCallback(() => {
    const fit = fitAddonRef.current;
    const container = containerRef.current;
    if (!fit || !container) return;
    if (container.offsetWidth === 0 || container.offsetHeight === 0) return;
    try {
      fit.fit();
    } catch {
      // fit() can throw if container is in a bad state
    }
  }, []);

  /**
   * Fit across animation frames until the container's measured size stops
   * changing, then invoke `onStable`. A single fit at a fixed delay is
   * unreliable on WebKitGTK (Linux/Tauri), where flex layout can still be
   * settling — and once the early fit locks in a transient size, xterm's
   * onResize won't fire again if the corrected size rounds to the same
   * cols/rows, so the PTY never learns the real dimensions (the "terminal
   * forgets its size after switching tabs" bug). On fast WebViews the size
   * is already stable on frame 1, so this fits once and stops immediately —
   * behavior there is unchanged. Caps at a handful of frames as a backstop.
   */
  const fitUntilStable = useCallback((onStable?: () => void) => {
    const container = containerRef.current;
    if (!container) { onStable?.(); return; }
    let lastW = -1;
    let lastH = -1;
    let frames = 0;
    const step = () => {
      const w = container.offsetWidth;
      const h = container.offsetHeight;
      frames += 1;
      if (w === 0 || h === 0) {
        // Not laid out yet — keep waiting a few frames for it to appear.
        if (frames < 12) requestAnimationFrame(step);
        else onStable?.();
        return;
      }
      doFit();
      if ((w !== lastW || h !== lastH) && frames < 12) {
        lastW = w;
        lastH = h;
        requestAnimationFrame(step);
      } else {
        onStable?.();
      }
    };
    requestAnimationFrame(step);
  }, [doFit]);

  // Mobile: resize wrapper when virtual keyboard opens/closes
  useEffect(() => {
    if (!isTouchDevice || !window.visualViewport) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const onViewportResize = () => {
      const vv = window.visualViewport!;
      // Set wrapper height to visual viewport height minus its offset from top
      const rect = wrapper.getBoundingClientRect();
      const available = vv.height - Math.max(0, rect.top - vv.offsetTop);
      wrapper.style.height = `${Math.max(100, available)}px`;
      doFit();
    };

    window.visualViewport.addEventListener('resize', onViewportResize);
    window.visualViewport.addEventListener('scroll', onViewportResize);
    return () => {
      window.visualViewport!.removeEventListener('resize', onViewportResize);
      window.visualViewport!.removeEventListener('scroll', onViewportResize);
      wrapper.style.height = '';
    };
  }, [isTouchDevice, doFit]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // State managed by the async init function
    let term: Terminal | null = null;
    let fitAddon: FitAddon | null = null;
    let disposed = false;
    let onDataDisposable: { dispose: () => void } | null = null;
    let onResizeDisposable: { dispose: () => void } | null = null;
    let unsubData: (() => void) | null = null;
    let unsubExit: (() => void) | null = null;
    let observer: ResizeObserver | null = null;
    let writeCoalesceTimer: ReturnType<typeof setTimeout> | null = null;
    let writeBufferStart = 0;
    let pendingFlushTimer: ReturnType<typeof setTimeout> | null = null;
    let resizeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    const init = async () => {
      // Wait for the terminal font to load before creating xterm.js.
      // xterm.js measures character cell dimensions at creation time;
      // if the font loads later, measurements are stale and the cursor
      // renders at wrong positions. JetBrains Mono is loaded via Google
      // Fonts in index.html — this resolves instantly if already cached.
      try {
        const size = isMobile ? 12 : 14;
        await Promise.all([
          document.fonts.load(`${size}px "JetBrains Mono"`),
          document.fonts.load(`bold ${size}px "JetBrains Mono"`),
        ]);
      } catch {
        // Font unavailable — xterm.js will fall back to system monospace
      }

      if (disposed) return;

      term = new Terminal({
        theme: xtermTheme,
        fontFamily: '"JetBrains Mono", "Cascadia Code", "Fira Code", monospace',
        fontSize: isMobile ? 12 : 14,
        cursorBlink: true,
        allowProposedApi: true,
        scrollback: 10000,
      });
      termRef.current = term;

      fitAddon = new FitAddon();
      fitAddonRef.current = fitAddon;
      term.loadAddon(fitAddon);

      term.open(container);

      // Mobile predictive text is hostile to xterm's hidden input textarea:
      // the keyboard's suggestion strip composes against the textarea, and
      // tapping a suggestion re-sends the whole composing region — duplicating
      // text that was already sent to the PTY keystroke-by-keystroke. A terminal
      // never wants autocomplete/autocorrect/predictions anyway, so turn every
      // predictive affordance off on the textarea. xterm already sets
      // autocorrect/autocapitalize/spellcheck; autocomplete is the one it omits.
      // On most keyboards this hides the suggestion strip entirely (nothing to
      // mis-tap); some (Gboard) may still show it — see the mobile input-bar
      // follow-up if so.
      const mobileTextarea = isMobile ? (term.textarea ?? null) : null;
      if (mobileTextarea) {
        mobileTextarea.setAttribute('autocomplete', 'off');
        mobileTextarea.setAttribute('autocorrect', 'off');
        mobileTextarea.setAttribute('autocapitalize', 'none');
        mobileTextarea.setAttribute('spellcheck', 'false');
      }

      // Autocomplete/suggestion INTERCEPTOR (mobile). If a keyboard still shows
      // its suggestion strip, tapping a suggestion fires a `beforeinput` with
      // inputType 'insertReplacementText'. Left alone, xterm sends that whole
      // replacement on TOP of the keystrokes it already forwarded → the word gets
      // duplicated with the suggestion appended. We catch ONLY that event, cancel
      // xterm's default handling, and send the terminal a clean "replace the
      // current word": erase the characters being replaced, then type the
      // suggestion. Every other inputType is ignored, so normal typing and IME
      // composition are completely unaffected (bounded blast radius).
      const onBeforeInput = (ev: Event) => {
        const ie = ev as InputEvent;
        if (ie.inputType !== 'insertReplacementText') return;
        const replacement = ie.data ?? '';
        // How many characters are being replaced (the current word). Prefer the
        // exact target range; if the browser doesn't expose it, fall back to a
        // word-delete (Ctrl+W).
        let replacedLen = -1;
        const ranges = typeof ie.getTargetRanges === 'function' ? ie.getTargetRanges() : [];
        if (ranges.length > 0) {
          replacedLen = Math.max(0, ranges[0].endOffset - ranges[0].startOffset);
        }
        ev.preventDefault();
        ev.stopImmediatePropagation();
        if (replacedLen < 0) {
          writeToPty('\x17' + replacement);              // Ctrl+W (delete word) + suggestion
        } else {
          writeToPty('\x7f'.repeat(replacedLen) + replacement); // DEL × N + suggestion
        }
      };
      if (mobileTextarea) {
        mobileTextarea.addEventListener('beforeinput', onBeforeInput, { capture: true });
      }

      // xterm.js 6.0.0's built-in DECRQM handler (CSI ? Ps $ p — "request mode") is
      // broken under our bundler: its DECRPM-value enum loses its declaration when
      // re-minified, so it throws `ReferenceError: n is not defined` and kills the
      // parser the instant any program sends a mode-query. opentui (OpenCode) probes
      // modes this way at startup, which (a) blanked OpenCode entirely and (b) — once
      // we suppressed the crash with a no-op — left the probes UNANSWERED, so OpenCode
      // concluded the terminal can't report focus/cursor state and stopped blinking
      // its cursor. So we reimplement DECRQM here: answer each query with a real
      // DECRPM reply. Wrapped in try/catch so it can never crash the parser — worst
      // case it just doesn't reply (same safety as a no-op, terminal still renders).
      term.parser.registerCsiHandler({ prefix: '?', intermediates: '$', final: 'p' }, (params) => {
        try {
          const core = (term as unknown as { _core: Record<string, unknown> })._core;
          const cs = core.coreService as { decPrivateModes: Record<string, boolean>; isCursorHidden: boolean; triggerDataEvent: (d: string) => void };
          const dpm = cs.decPrivateModes;
          const opt = (core.optionsService as { rawOptions: Record<string, unknown> }).rawOptions;
          const mouse = core.coreMouseService as { activeProtocol: string; activeEncoding: string };
          const buffers = (core._bufferService as { buffers: { active: unknown; alt: unknown } }).buffers;
          const u = (b: boolean) => (b ? 1 : 2); // 1 = set, 2 = reset
          const raw = params[0];
          const mode = Array.isArray(raw) ? raw[0] : raw;
          if (typeof mode !== 'number') return true; // malformed query — handled, no reply
          let v: number;
          switch (mode) {
            case 1: v = u(dpm.applicationCursorKeys); break;
            case 6: v = u(dpm.origin); break;
            case 7: v = u(dpm.wraparound); break;
            case 9: v = u(mouse.activeProtocol === 'X10'); break;
            case 12: v = u(Boolean(opt.cursorBlink)); break;
            case 25: v = u(!cs.isCursorHidden); break;
            case 45: v = u(dpm.reverseWraparound); break;
            case 66: v = u(dpm.applicationKeypad); break;
            case 1000: v = u(mouse.activeProtocol === 'VT200'); break;
            case 1002: v = u(mouse.activeProtocol === 'DRAG'); break;
            case 1003: v = u(mouse.activeProtocol === 'ANY'); break;
            case 1004: v = u(dpm.sendFocus); break;
            case 1006: v = u(mouse.activeEncoding === 'SGR'); break;
            case 1016: v = u(mouse.activeEncoding === 'SGR_PIXELS'); break;
            case 47:
            case 1047:
            case 1049: v = u(buffers.active === buffers.alt); break;
            case 2004: v = u(dpm.bracketedPasteMode); break;
            case 2026: v = u(dpm.synchronizedOutput); break;
            case 8: v = 3; break;
            case 67:
            case 1005:
            case 1015: v = 4; break;
            case 1048: v = 1; break;
            default: v = 0; // not recognized
          }
          cs.triggerDataEvent(`\x1b[?${mode};${v}$y`);
        } catch {
          /* never let a reply attempt crash the parser */
        }
        return true; // handled — overrides xterm's broken built-in
      });

      // --- Link detection ---

      // Helper: open URL in a new tab. (The CEF host opens this in the system
      // browser via its own popup/navigation handling.)
      const openUrl = (uri: string) => {
        window.open(uri, '_blank', 'noopener,noreferrer');
      };

      // 1. WebLinksAddon handles URLs (http/https)
      const webLinksAddon = new WebLinksAddon((_event, uri) => openUrl(uri));
      term.loadAddon(webLinksAddon);

      // Path routing (ask: terminal link should open in the right place).
      // A path INSIDE the current project opens in the Files panel; a path
      // OUTSIDE it (e.g. a /tmp screenshot/artifact, Downloads, another project)
      // opens with the OS default app via the host's open-external bridge.
      const isAbsolutePath = (p: string) => /^([A-Za-z]:[\\/]|\/)/.test(p);
      const normSlash = (p: string) => p.replace(/\\/g, '/').replace(/\/+$/, '');
      const isWithinProject = (absPath: string, root: string) => {
        if (!root) return false;
        const r = normSlash(root);
        const a = normSlash(absPath);
        return a === r || a.startsWith(r + '/');
      };
      // Route a matched path to the Files panel (in-project / relative) or to the
      // OS default handler (absolute + outside the project). Strips a trailing
      // :line:col before opening.
      const routePath = (raw: string, projectRoot: string) => {
        const cleanPath = raw.replace(/:\d+(?::\d+)?$/, '');
        if (isAbsolutePath(cleanPath) && !isWithinProject(cleanPath, projectRoot)) {
          // file:// URL so the OS routes it to the default app; %20-encode spaces.
          const url = /^[A-Za-z]:[\\/]/.test(cleanPath)
            ? encodeURI('file:///' + cleanPath.replace(/\\/g, '/'))
            : encodeURI('file://' + cleanPath);
          backend.files.openExternal(url).catch((e) => logger.warn(TAG, 'openExternal failed', e));
          return;
        }
        useFileOpenStore.getState().requestOpenFile(cleanPath, projectRoot);
      };

      // 2. Custom provider for file paths — routes per routePath() above.
      // Windows paths can contain spaces (e.g., C:\projects\Resource Mulch\file.docx).
      // We detect paths that start with a drive letter or ./ ../ ~/ prefix.
      const filePathPatterns = [
        // Windows absolute paths (may contain spaces): C:\projects\Resource Mulch\file.docx
        /(?<![a-zA-Z0-9_])[A-Za-z]:[\\\/](?:[^<>"|?*\n]+[\\\/])*[^<>"|?*\n\s.]+(?:\.[a-zA-Z0-9]+)+(?::(\d+)(?::(\d+))?)?/g,
        // Unix absolute paths: /path/to/file.ts:42:10
        /(?<![a-zA-Z0-9_:])\/(?:[^\s<>:"|?*(){}\\]+\/)*[^\s<>:"|?*(){}\\]+\.[a-zA-Z0-9]+(?::(\d+)(?::(\d+))?)?/g,
        // Relative paths: ./foo, ../foo/bar.ts, ~/foo/bar
        /(?<![a-zA-Z0-9_\\\/])(?:\.{1,2}|~)[\\\/][^\n<>"|?*]+\.[a-zA-Z0-9]+(?::(\d+)(?::(\d+))?)?/g,
        // Bare relative with extension: src/lib/foo.ts:42 (no spaces, must have / and .ext)
        /(?<![a-zA-Z0-9_\\\/.-])(?:[a-zA-Z_][a-zA-Z0-9_.-]*[\\\/])+[a-zA-Z0-9_.-]+\.[a-zA-Z0-9]{1,10}(?::(\d+)(?::(\d+))?)?/g,
      ];

      const filePathProvider: ILinkProvider = {
        provideLinks: (bufferLineNumber: number, callback: (links: ILink[] | undefined) => void) => {
          // bufferLineNumber is 1-based, but getLine() expects a 0-based index
          const line = term!.buffer.active.getLine(bufferLineNumber - 1);
          if (!line) { callback(undefined); return; }

          const text = line.translateToString(true);
          const links: ILink[] = [];
          const session = useTerminalStore.getState().sessions.get(sessionId);
          const projectRoot = session?.projectPath || '';

          const used = new Set<number>();

          // 0. Quoted / backtick path spans FIRST — the quotes bound the path, so
          // a space inside (e.g. "/tmp/My Screenshot.png", `C:\A B\x.docx`) no
          // longer splits it into multiple broken links. We linkify the inner
          // path (without the surrounding quotes) and reserve those columns so the
          // unquoted regexes below don't re-match fragments of it.
          const quotedRe = /(['"`])((?:(?!\1)[^\n])*?[\\/](?:(?!\1)[^\n])*?\.[A-Za-z0-9]{1,10}(?::\d+(?::\d+)?)?)\1/g;
          let qm: RegExpExecArray | null;
          while ((qm = quotedRe.exec(text)) !== null) {
            const inner = qm[2];
            const innerStart = qm.index + 1; // skip the opening quote
            if (inner.length < 4) continue;
            if (used.has(innerStart)) continue;
            for (let i = qm.index; i < qm.index + qm[0].length; i++) used.add(i);
            links.push({
              range: {
                start: { x: innerStart + 1, y: bufferLineNumber },
                end: { x: innerStart + inner.length + 1, y: bufferLineNumber },
              },
              text: inner,
              activate: () => routePath(inner, projectRoot),
            });
          }

          for (const pattern of filePathPatterns) {
            pattern.lastIndex = 0;
            let match: RegExpExecArray | null;
            while ((match = pattern.exec(text)) !== null) {
              let matchText = match[0];
              const startCol = match.index;

              if (matchText.length < 4) continue;
              if (used.has(startCol)) continue;

              // Skip URLs (handled by WebLinksAddon)
              if (/https?:\/\//i.test(text.slice(Math.max(0, startCol - 8), startCol + matchText.length))) continue;

              // Strip trailing punctuation
              matchText = matchText.replace(/[,;)}\]\s.]+$/, '');

              for (let i = startCol; i < startCol + matchText.length; i++) used.add(i);

              const linkText = matchText;
              links.push({
                range: {
                  start: { x: startCol + 1, y: bufferLineNumber },
                  end: { x: startCol + matchText.length + 1, y: bufferLineNumber },
                },
                text: matchText,
                activate: () => routePath(linkText, projectRoot),
              });
            }
          }

          callback(links.length > 0 ? links : undefined);
        },
      };
      term.registerLinkProvider(filePathProvider);

      // Restore buffered output from disk. The disk buffer always has the
      // full terminal history (Rust writes to disk for both attached and
      // detached terminals). getBuffer() reads it without changing attach
      // state — TerminalTab handles attach/detach at the project level.
      try {
        const buffered = await backend.terminal.getBuffer(sessionId);
        if (disposed) return;
        if (buffered) {
          const raw = atob(buffered);
          const bytes = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; i++) {
            bytes[i] = raw.charCodeAt(i);
          }
          term.write(bytes);
        }
      } catch {
        if (disposed) return;
        // getBuffer failed — PTY likely died while detached
        const session = useTerminalStore.getState().sessions.get(sessionId);
        if (session && session.status === 'active') {
          updateSessionStatus(sessionId, 'closed');
          term.write('\r\n\x1b[90m[Session ended]\x1b[0m\r\n');
        }
      }

      if (disposed) return;

      // --- Paste & Copy handling ---
      // All paste goes through exactly ONE handler on the container (capture phase).
      // Using stopImmediatePropagation ensures xterm's internal paste handler
      // never fires, preventing duplicate writes to the PTY.
      let ctrlVHandled = false;

      // Handle Ctrl+V and Ctrl+C via key handler
      term.attachCustomKeyEventHandler((event) => {
        if (event.type !== 'keydown') return true;
        const isCtrlOrMeta = event.ctrlKey || event.metaKey;

        if (isCtrlOrMeta && event.key === 'v') {
          // Mark that we're handling Ctrl+V so the paste event handler
          // (which may also fire) knows not to double-paste.
          ctrlVHandled = true;
          event.preventDefault();
          // Handles both cases: a clipboard IMAGE becomes a saved attachment
          // whose path is inserted; otherwise a normal text paste (+ history).
          pasteFromClipboard()
            .catch((e) => logger.warn(TAG, 'clipboard paste failed', e))
            .finally(() => {
              setTimeout(() => { ctrlVHandled = false; }, 50);
            });
          return false;
        }

        if (isCtrlOrMeta && event.key === 'c' && term!.hasSelection()) {
          clipboardWrite(backend, term!.getSelection()).catch((e) => logger.warn(TAG, 'clipboard write failed', e));
          term!.clearSelection();
          return false;
        }

        return true;
      });

      // Single paste event handler — catches right-click paste and any paste
      // events that slip through despite the Ctrl+V handler above.
      const onPaste = (e: ClipboardEvent) => {
        e.preventDefault();
        e.stopImmediatePropagation(); // Block xterm's internal paste handler
        if (ctrlVHandled) return; // Already handled by Ctrl+V above

        // Check for image content in the clipboard (e.g. screenshot via Win+Shift+S).
        // If present, save it as a project attachment and insert the resulting path
        // so the agent can actually see the image.
        const items = e.clipboardData?.items;
        if (items) {
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === 'file' && item.type.startsWith('image/')) {
              const file = item.getAsFile();
              if (!file) continue;
              const sess = useTerminalStore.getState().sessions.get(sessionId);
              const pid = sess?.projectPath ? projectIdFromPath(sess.projectPath) : null;
              if (!pid) break; // can't save without a project ID
              saveImageAsAttachment(file, pid, backend)
                .then((saved) => {
                  if (saved && term && !disposed) {
                    term.paste(quoteIfSpaced(saved));
                  }
                })
                .catch((err) => logger.warn(TAG, 'Image paste upload failed', err));
              return;
            }
          }
        }

        const text = e.clipboardData?.getData('text');
        if (text) {
          const sess = useTerminalStore.getState().sessions.get(sessionId);
          if (sess?.projectPath) {
            useInputHistoryStore.getState().addEntry(text, 'paste', sess.projectPath);
          }
          term!.paste(text);
        }
      };
      container.addEventListener('paste', onPaste as EventListener, { capture: true });

      // Manual touch scrolling. xterm 6 ships a native gesture scroller, but in
      // our layout the canvas (`.xterm-screen`) sits on top of the scrollable
      // `.xterm-viewport`, so touches land on the canvas and never reach the
      // native scroller — the swipe falls through to the browser, which does
      // nothing to the buffer and triggers pull-to-refresh on a downward swipe.
      // So we OWN vertical touch scrolling here: on a real drag we preventDefault
      // (kills pull-to-refresh / page scroll) AND stopPropagation in the CAPTURE
      // phase, so xterm's own document-level gesture handler never ALSO fires —
      // that double-handling is what made scrolling erratic before this. A small
      // start threshold lets taps pass through (so tap-to-focus still works), and
      // the touchend guard stops xterm reading the end of a scroll as a tap
      // (which would pop the on-screen keyboard). `touch-action: none` on the
      // container (set in JSX) is the CSS backstop that also blocks pull-to-
      // refresh during the sub-threshold window before the JS kicks in.
      let touchStartY = 0;
      let touchAccum = 0;
      let touchScrolling = false;
      const TOUCH_SCROLL_THRESHOLD = 6; // px of drag before it counts as a scroll

      const onTouchStart = (e: TouchEvent) => {
        if (e.touches.length === 1) {
          touchStartY = e.touches[0].clientY;
          touchAccum = 0;
          touchScrolling = false;
        }
      };
      const onTouchMove = (e: TouchEvent) => {
        if (!term || e.touches.length !== 1) return;
        const y = e.touches[0].clientY;
        touchAccum += touchStartY - y;
        touchStartY = y;
        if (!touchScrolling && Math.abs(touchAccum) < TOUCH_SCROLL_THRESHOLD) return;
        touchScrolling = true;
        e.preventDefault();   // suppress pull-to-refresh / native page scroll
        e.stopPropagation();  // suppress xterm's built-in gesture (no double scroll)
        // Derive the row (cell) height from the live layout instead of a
        // hardcoded font size, so scroll speed tracks the finger naturally.
        const rowHeight = term.rows > 0 ? container.clientHeight / term.rows : 16;
        const lines = Math.trunc(touchAccum / rowHeight);
        if (lines !== 0) {
          touchAccum -= lines * rowHeight;
          const coreMouse = (term as unknown as {
            _core?: { coreMouseService?: { triggerMouseEvent: (ev: Record<string, unknown>) => boolean } };
          })._core?.coreMouseService;
          if (coreMouse && term.modes.mouseTrackingMode !== 'none') {
            // A full-screen app with mouse tracking is up (Claude Code, htop,
            // vim -mouse, less…). It repaints IN PLACE, which pollutes xterm's
            // scrollback with partial-redraw debris (stray line-number gutters,
            // half-drawn frames) — scrolling that buffer shows garbage, which is
            // exactly what the user hit. So DON'T scroll the buffer: forward the
            // gesture to the app as wheel input via xterm's mouse encoder (using
            // the app's negotiated protocol/encoding). Apps that honor the wheel
            // scroll their own view; ones that ignore it (Claude) just stay put —
            // and crucially the garbage scrollback is never revealed. button 4 =
            // wheel; action 1 = down (toward newer, finger up), 0 = up.
            const notches = Math.min(Math.abs(lines), 40);
            const action = lines > 0 ? 1 : 0;
            const rect = container.getBoundingClientRect();
            const colW = term.cols > 0 ? container.clientWidth / term.cols : 8;
            const col = Math.max(1, Math.min(term.cols, Math.ceil((e.touches[0].clientX - rect.left) / colW)));
            const row = Math.max(1, Math.min(term.rows, Math.ceil((y - rect.top) / rowHeight)));
            for (let k = 0; k < notches; k++) {
              coreMouse.triggerMouseEvent({ col, row, x: 0, y: 0, button: 4, action, ctrl: false, alt: false, shift: false });
            }
          } else {
            // Plain shell: scroll xterm's real scrollback (the good path).
            term.scrollLines(lines);
          }
        }
      };
      const onTouchEnd = (e: TouchEvent) => {
        if (touchScrolling) {
          e.stopPropagation(); // don't let xterm read the scroll's end as a tap
          touchScrolling = false;
        }
      };

      if (isMobile) {
        container.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
        container.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
        container.addEventListener('touchend', onTouchEnd, { capture: true });
      }

      // Track initial fit state — queue incoming data until the terminal
      // is fitted to the container. For local (Tauri) terminals, also wait
      // for the PTY resize to propagate before flushing.
      let initialFitDone = false;
      let pendingWrites: Uint8Array[] = [];

      // Forward user input → PTY stdin (base64-encoded)
      // On mobile, debounce to prevent IME composition from sending duplicate input
      const inputEncoder = new TextEncoder();
      let lastInput = '';
      let lastInputTime = 0;
      const DEDUP_WINDOW_MS = 50;

      onDataDisposable = term.onData((data) => {
        const now = Date.now();

        // Mobile IME deduplication
        if (isMobile && data === lastInput && now - lastInputTime < DEDUP_WINDOW_MS) {
          return; // Skip duplicate from IME composition
        }
        lastInput = data;
        lastInputTime = now;

        // Large data (typically a paste) — chunk it so the shell doesn't
        // drop the beginning. Single keystrokes go through the fast path
        // below (no artificial delay).
        if (data.length > PTY_WRITE_CHUNK_THRESHOLD) {
          writeTextToPty(data, sessionId, (id, payload) => backend.terminal.write(id, payload))
            .catch((e) => logger.warn(TAG, 'chunked PTY paste failed', e));
          return;
        }

        const bytes = inputEncoder.encode(data);
        const encoded = uint8ToBase64(bytes);
        backend.terminal.write(sessionId, encoded).catch((e) => logger.warn(TAG, 'PTY write failed', e));
      });

      // Coalesce PTY output so TUI redraws land in fewer writes.
      let writeBuffer: Uint8Array[] = [];

      const flushWrites = () => {
        writeCoalesceTimer = null;
        writeBufferStart = 0;
        if (disposed || !term || writeBuffer.length === 0) return;
        const totalLen = writeBuffer.reduce((sum, b) => sum + b.length, 0);
        const combined = new Uint8Array(totalLen);
        let offset = 0;
        for (const chunk of writeBuffer) {
          combined.set(chunk, offset);
          offset += chunk.length;
        }
        writeBuffer = [];
        term.write(combined);
      };

      const flushPendingWrites = () => {
        pendingFlushTimer = null;
        if (disposed) return;
        for (const chunk of pendingWrites) {
          writeBuffer.push(chunk);
        }
        pendingWrites = [];
        if (writeBuffer.length > 0) {
          flushWrites();
        }
      };

      unsubData = backend.terminal.onData((id, data) => {
        if (id !== sessionId || disposed) return;
        try {
          const raw = atob(data);
          const bytes = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; i++) {
            bytes[i] = raw.charCodeAt(i);
          }
          if (!initialFitDone) {
            pendingWrites.push(bytes);
            return;
          }
          writeBuffer.push(bytes);
          const now = Date.now();
          if (writeBufferStart === 0) writeBufferStart = now;
          // Debounced coalescing: wait for more data to accumulate so
          // entire TUI redraws land in a single write. Max 50ms buffer
          // to keep long streams (cat, build logs) flowing smoothly.
          if (now - writeBufferStart >= 50) {
            if (writeCoalesceTimer !== null) clearTimeout(writeCoalesceTimer);
            flushWrites();
          } else {
            if (writeCoalesceTimer !== null) clearTimeout(writeCoalesceTimer);
            writeCoalesceTimer = setTimeout(flushWrites, 8);
          }
        } catch {
          // invalid base64, skip
        }
      });

      // Listen for PTY exit
      unsubExit = backend.terminal.onExit((id) => {
        if (id !== sessionId || disposed || !term) return;
        term.write('\r\n\x1b[90m[Process exited]\x1b[0m\r\n');
        updateSessionStatus(id, 'closed');
      });

      // Report size changes on resize
      onResizeDisposable = term.onResize(({ cols, rows }) => {
        backend.terminal.resize(sessionId, cols, rows).catch((e) => {
          if (!handleSessionGone(e)) logger.warn(TAG, 'resize failed', e);
        });
      });

      // ResizeObserver for container dimension changes.
      // Handles initial fit + ongoing resizes. Debounced to prevent
      // fit oscillation during continuous resizes (panel drag, keyboard).
      observer = new ResizeObserver(() => {
        if (disposed) return;
        if (!initialFitDone && container.offsetWidth > 0 && container.offsetHeight > 0) {
          // First fit — run across frames until the size settles, then flush.
          // Guards against WebKitGTK reporting a transient pre-layout size.
          initialFitDone = true;
          fitUntilStable(() => {
            if (disposed || !term) return;
            const { cols, rows } = term;
            // Force SIGWINCH even if PTY was created at the same size:
            // briefly resize to cols-1 then to the real size. This ensures
            // the shell redraws its prompt and the cursor lands at the
            // correct position — critical for both new and reconnected sessions.
            backend.terminal.resize(sessionId, Math.max(1, cols - 1), rows)
              .then(() => backend.terminal.resize(sessionId, cols, rows))
              .catch((e) => {
                if (!handleSessionGone(e)) logger.warn(TAG, 'resize bounce failed', e);
              });
            pendingFlushTimer = setTimeout(flushPendingWrites, 80);
          });
        } else {
          if (resizeDebounceTimer) clearTimeout(resizeDebounceTimer);
          resizeDebounceTimer = setTimeout(() => doFit(), 80);
        }
      });
      observer.observe(container);

      // Stash the paste + touch handlers for cleanup on unmount.
      (container as HTMLDivElement & { _paneCleanup?: () => void })._paneCleanup = () => {
        container.removeEventListener('paste', onPaste, { capture: true } as EventListenerOptions);
        if (mobileTextarea) mobileTextarea.removeEventListener('beforeinput', onBeforeInput, { capture: true } as EventListenerOptions);
        if (isMobile) {
          const cap = { capture: true } as EventListenerOptions;
          container.removeEventListener('touchstart', onTouchStart, cap);
          container.removeEventListener('touchmove', onTouchMove, cap);
          container.removeEventListener('touchend', onTouchEnd, cap);
        }
      };

      // Initial focus on mount — TerminalView only mounts when its session
      // becomes active (sessionId is the React key on the wrapper), so by
      // definition the user just switched to this tab and wants to type.
      // The isActive useEffect below ALSO calls focus() but its 50ms timer
      // can fire before init completes (font load can be slow), missing
      // termRef. This is the dependable mount-side focus.
      if (!disposed) {
        term.focus();
      }
    };

    init();

    return () => {
      disposed = true;
      if (pendingFlushTimer !== null) clearTimeout(pendingFlushTimer);
      if (resizeDebounceTimer !== null) clearTimeout(resizeDebounceTimer);
      if (writeCoalesceTimer !== null) clearTimeout(writeCoalesceTimer);
      observer?.disconnect();
      onDataDisposable?.dispose();
      onResizeDisposable?.dispose();
      unsubData?.();
      unsubExit?.();
      const paneCleanup = (container as HTMLDivElement & { _paneCleanup?: () => void })._paneCleanup;
      if (paneCleanup) {
        paneCleanup();
        delete (container as HTMLDivElement & { _paneCleanup?: () => void })._paneCleanup;
      }
      term?.dispose();
      termRef.current = null;
      fitAddonRef.current = null;
    };
  }, [sessionId, backend, updateSessionStatus, doFit, fitUntilStable, handleSessionGone, pasteFromClipboard]);

  const sendKey = useCallback((key: MobileKey) => {
    if (key.action === 'scrollToTop') {
      termRef.current?.scrollToTop();
    } else if (key.action === 'scrollToBottom') {
      termRef.current?.scrollToBottom();
    } else if (key.data) {
      writeToPty(key.data);
    }
  }, [writeToPty]);

  // Re-fit, refresh, and force SIGWINCH when this terminal becomes active.
  // While hidden, writes still flow to xterm.js and the cursor may end up
  // at an intermediate position. The SIGWINCH forces the TUI to redraw,
  // repositioning its cursor at the input area.
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    // Re-fit across frames (term may still be initializing or the layout may
    // still be settling), then refresh + force SIGWINCH once the size is stable.
    fitUntilStable(() => {
      if (cancelled) return;
      const term = termRef.current;
      if (!term) return;
      term.refresh(0, term.rows - 1); // force canvas re-render
      term.focus();
      // Two-step resize to guarantee SIGWINCH even if size hasn't changed
      const { cols, rows } = term;
      backend.terminal.resize(sessionId, Math.max(1, cols - 1), rows)
        .then(() => backend.terminal.resize(sessionId, cols, rows))
        .catch((e) => {
          if (!handleSessionGone(e)) logger.warn(TAG, 'active resize failed', e);
        });
    });
    return () => { cancelled = true; };
  }, [isActive, fitUntilStable, backend, sessionId, handleSessionGone]);

  // Re-focus when requested by another component (e.g. after file picker closes)
  const focusTrigger = useTerminalStore((s) => s.focusTrigger);
  useEffect(() => {
    if (!isActive || focusTrigger === 0) return;
    requestAnimationFrame(() => {
      termRef.current?.focus();
    });
  }, [focusTrigger, isActive]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, hasSelection: !!termRef.current?.hasSelection() });
  }, []);

  const ctxCopy = useCallback(() => {
    const term = termRef.current;
    if (term?.hasSelection()) {
      clipboardWrite(backend, term.getSelection()).catch((err) => logger.warn(TAG, 'copy failed', err));
      term.clearSelection();
    }
    setCtxMenu(null);
  }, [backend]);

  const ctxPaste = useCallback(() => {
    // Same image-aware path as Ctrl+V.
    pasteFromClipboard().catch((err) => logger.warn(TAG, 'paste failed', err));
    setCtxMenu(null);
    termRef.current?.focus();
  }, [pasteFromClipboard]);

  const ctxSelectAll = useCallback(() => {
    termRef.current?.selectAll();
    setCtxMenu(null);
  }, []);

  if (isPending) return <TerminalViewLoading />;

  return (
    <div ref={wrapperRef} className="flex flex-col w-full h-full" style={{ minHeight: 0 }}>
      {isTouchDevice && (
        <div className="flex items-center gap-1 px-1.5 py-1 bg-stone-800 border-b border-stone-700 overflow-x-auto shrink-0">
          {MOBILE_KEYS.map((k) => (
            <button
              key={k.label}
              onTouchEnd={(e) => { e.preventDefault(); sendKey(k); }}
              onClick={(e) => { e.preventDefault(); }} // prevent ghost click
              className="px-2 py-0.5 text-[11px] text-stone-300 bg-stone-700 rounded active:bg-stone-600 whitespace-nowrap select-none"
            >
              {k.label}
            </button>
          ))}
        </div>
      )}
      <div
        ref={containerRef}
        className={`flex-1 min-h-0 ${dragOver ? 'ring-2 ring-inset ring-amber-500/50' : ''}`}
        // touch-action:none makes us the sole owner of touch here: no browser
        // pull-to-refresh, page-scroll, or double-tap-zoom over the terminal —
        // vertical scrolling is done manually in the touch handlers above.
        style={isTouchDevice ? { touchAction: 'none' } : undefined}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onContextMenu={handleContextMenu}
      />
      {ctxMenu && (
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setCtxMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }}
          />
          <div
            className="fixed z-[9999] min-w-[150px] py-1 bg-stone-800 border border-stone-600 rounded-md shadow-xl text-sm text-stone-200 select-none"
            style={{ left: ctxMenu.x, top: ctxMenu.y }}
          >
            <button
              type="button"
              disabled={!ctxMenu.hasSelection}
              onClick={ctxCopy}
              className="w-full text-left px-3 py-1.5 hover:bg-stone-700 disabled:opacity-40 disabled:hover:bg-transparent flex justify-between gap-6"
            >
              <span>Copy</span><span className="text-stone-500">Ctrl+C</span>
            </button>
            <button
              type="button"
              onClick={ctxPaste}
              className="w-full text-left px-3 py-1.5 hover:bg-stone-700 flex justify-between gap-6"
            >
              <span>Paste</span><span className="text-stone-500">Ctrl+V</span>
            </button>
            <div className="my-1 border-t border-stone-700" />
            <button
              type="button"
              onClick={ctxSelectAll}
              className="w-full text-left px-3 py-1.5 hover:bg-stone-700"
            >
              Select All
            </button>
          </div>
        </>
      )}
    </div>
  );
});
